import { FORTA_BLACK, FORTA_ERROR } from '../../../../utils/colors';
import {
  DATE_TIME_PICKER_INPUT_HEIGHT,
  INPUT_LABEL_NUDGE,
  NOTCHED_OUTLINE_CLASS_NAME,
} from '../constants';

import type { SxProps, Theme } from '@mui/material';
import type { CSSProperties } from 'react';
import type { NotchedOutlineVisualState, PickerVisualState } from '../types';

export function buildDateTimePickerStyles(state: PickerVisualState): CSSProperties {
  const borderColor = state.error ? FORTA_ERROR : FORTA_BLACK;

  return {
    width: state.fullWidth ? '100%' : undefined,
    height: DATE_TIME_PICKER_INPUT_HEIGHT,
    borderRadius: 6,
    // In cutout mode the notched fieldset draws the border; the picker itself stays borderless and
    // transparent so the surface shows through exactly once — matching the (transparent) text field.
    // A colored fill here would double up on translucent row backgrounds and read as darker.
    border: state.borderCutout ? 'none' : `1px solid ${borderColor}`,
    backgroundColor: state.borderCutout ? 'transparent' : undefined,
    transition: 'border-color 150ms',
    boxShadow: state.disabled ? undefined : 'none',
    opacity: state.disabled ? 0.38 : undefined,
    cursor: state.disabled ? 'not-allowed' : undefined,
  };
}

export function buildNotchedOutlineSx(state: NotchedOutlineVisualState): SxProps<Theme> {
  return {
    [`& .${NOTCHED_OUTLINE_CLASS_NAME}`]: {
      borderColor: state.error ? FORTA_ERROR : FORTA_BLACK,
      opacity: state.disabled ? 0.38 : undefined,
    },
  };
}

export function buildDateTimePickerInputLabelSx(
  inputLabelUserSx: SxProps<Theme> | undefined,
  borderCutout: boolean,
): SxProps<Theme> {
  return [
    {
      position: 'absolute',
      top: 0,
      left: '14px',
      zIndex: 1,
      transformOrigin: 'top left',
      maxWidth: 'calc(100% - 24px)',
      pointerEvents: 'none',
      lineHeight: 1.4375,
      color: FORTA_BLACK,
      // In cutout mode the label must line up with the notch that the legend carves. The legend
      // renders a native 0.75rem font, so the visible label renders the same way (no `scale(0.75)`
      // of a 1rem font, which would come out slightly wider and push the text past the notch's
      // right edge) and drops the fill + horizontal padding that would offset it. The fill version
      // keeps the scaled font and padding so the background wraps the text.
      ...(borderCutout
        ? { fontSize: '0.75rem', transform: `translate(0, ${INPUT_LABEL_NUDGE}px)`, px: 0 }
        : {
            transform: `translate(0, ${INPUT_LABEL_NUDGE}px) scale(0.75)`,
            px: 0.5,
            bgcolor: 'background.paper',
          }),
    },
    ...(inputLabelUserSx != null
      ? Array.isArray(inputLabelUserSx)
        ? inputLabelUserSx
        : [inputLabelUserSx]
      : []),
  ];
}
